import React, {useEffect, useMemo, useState} from 'react';
import {graphql, useFragment} from 'react-relay';
import * as tracking from '../../../tracking';
import loadScript from 'load-script';
import {useTrackPage} from '../../../tracking/amplitude/hooks/useTrackPage';
import DirectApi from '../../../directApi';
import * as Sentry from '@sentry/browser';
import styled from 'styled-components';
import {RadioButton} from '@/web-components';
import {
	settingsForNewProjectsAndTasks,
	settingsForExistingProjectsAndTasks,
	settingsForNewPhasesTasksAndTimeRegsOnExistingProjects,
} from './SettingsUtil';
import {FlexRow, Link, DeprecatedText as Text} from '@forecast-it/design-system';
import TooltipContainer from '../../shared/components/tooltips/tooltip_container';
import DataImportValidator from './DataImportValidator';
import {Team} from '../../../sentry/GetTeamFromPathname';
import ImportMode from './ImportMode';
import type {SettingsDataImporter_viewer$key as SettingsDataImporter_viewer} from './__generated__/SettingsDataImporter_viewer.graphql';
import {NuvoImporter, NuvoImportResult, Settings} from './Types';
import {SubmitDataImport} from './SubmitDataImport';
import {useIntl} from 'react-intl';

/**
 * Important information about this module:
 *
 * We are currently using a deprecated version of nuvo-vanilla-js: 2.8.0
 *
 * Version 2.9 has some major API changes mainly around initialization of validation hooks.
 * We have two options to upgrade:
 *
 * 	- Switch to the react version
 * 	- Upgrade to the latest version of the vanilla version
 *
 * 	The react version currently crashes at the last page in the import - but we can check back on this at a later date.
 *
 * Updating to 2.9 vanilla, requires some changes to the way we load the script as it is now a module:
 * loadScript('https://unpkg.com/nuvo-vanilla-js@2.9.0', {type: 'module'}, this.onApiLoad);
 *
 * We might also need to introduce a custom script loader to handle the new module type.
 *
 * see https://socket.dev/npm/package/nuvo-vanilla-js
 *
 * A script could be
 *
 * <script type="module">
 *   import { launchNuvoImporter } from 'https://unpkg.com/nuvo-vanilla-js';
 *   window.launchNuvoImporter = launchNuvoImporter;
 * </script>
 * and then we can access the launchNuvoImporter function from the window object in the react code
 *
 * Would be much nicer to just use the react component.
 *
 *
 * for reference this is the syntax to lazy load the react module:
 *
 *  const NuvoImporter = lazy(() =>
 *  import('nuvo-react').then(module => {
 *    return {default: module.NuvoImporter};
 *  }
 *
 *  and then wrap the NuvoImporter component in a Suspense component in render.
 *
 *  it shows up but crashes at the last page.
 *
 * */
declare global {
	interface Window {
		nuvo: {
			NuvoImporter: {
				new (key: string, settings: Settings): NuvoImporter;
			};
		};
	}
}

export const DescriptionStyled = styled.div`
	.description {
		padding-left: 38px;
		padding-right: 0px;
		padding-top: 16px;
		padding-bottom: 16px;
	}
	.header {
		display: flex;
		flex-direction: column;
		padding-left: 38px;
		padding-right: 0px;
		padding-top: 20px;
		padding-bottom: 0px;
	}
	.header-title {
		display: flex;
		color: #535353;
		font-size: 20px;
		font-weight: 500;
	}
	.sub-description {
		padding-left: 38px;
		padding-right: 0px;
		padding-top: 15px;
	}
	.bold {
		font-weight: bold;
		padding-top: 15px;
		margin: 0px;
	}
	.button {
		display: flex;
		justify-content: flex-start;
		padding-left: 38px;
		margin-top: 20px;
		gap: 20px;
	}
	.my-nuvo-style1 {
		position: relative;
		max-height: 500px;
		max-width: 80vw;
		overflow: scroll;
	}
`;

export const RateCardsStyle = styled.div`
	display: flex;
	flex-direction: column;
	flex-grow: 1;
	padding-bottom: 10px;
`;

export const Section = styled.div`
	display: flex;
	max-width: 500px;
	flex-direction: column;
	flex-wrap: wrap;
	gap: 8px;
`;

export const SubSection = styled.div`
	display: flex;
	flex-direction: column;
	flex-wrap: wrap;
	gap: 12px;
	padding-left: 8px;
	padding-top: 8px;
`;

const PAGE_NAME = 'Data Importer';

const TopHeader = (): React.JSX.Element => {
	const intl = useIntl();
	return (
		<div className="header">
			<div>
				<TooltipContainer
					infoText={[
						{
							unstyledList: [
								'Here you can import / migrate data to your account. For details see our guide ',
								<a target="_blank" href="https://support.forecast.app/hc/en-us">
									https://support.forecast.app
								</a>,
							],
							noBox: true,
						},
					]}
					edgeOffset={350}
				>
					<a className="header-title">Data Import</a>
				</TooltipContainer>
			</div>
			<Text size={2}>
				{intl.formatMessage({id: 'admin.data_importer.subtitle'})}&nbsp;
				<Link
					href={'https://support.forecast.app/hc/en-us/articles/12447804765713-Importing-data-into-Forecast'}
					target={'_blank'}
					rel="noopener noreferrer"
					size="2"
				>
					{intl.formatMessage({id: 'admin.data_importer.subtitle_link'})}
				</Link>
			</Text>
		</div>
	);
};

const ImportSelection = ({
	importMode,
	setImportMode,
	disabled = false,
}: {
	importMode: ImportMode;
	setImportMode: (mode: ImportMode) => void;
	disabled?: boolean;
}): React.JSX.Element => {
	return (
		<Section>
			<SubSection>
				<FlexRow gap={'s'}>
					<RadioButton
						checked={importMode === ImportMode.ImportNewProjects}
						onClick={() => setImportMode(ImportMode.ImportNewProjects)}
						disabled={disabled}
					/>
					<Text>
						Import{' '}
						<b>
							<u>new</u>
						</b>{' '}
						projects, tasks and time entries.
					</Text>
				</FlexRow>
				<FlexRow gap={'s'}>
					<RadioButton
						checked={importMode === ImportMode.ImportTimeRegs}
						onClick={() => setImportMode(ImportMode.ImportTimeRegs)}
						disabled={disabled}
					/>
					<Text>
						Import time entries into{' '}
						<b>
							<u>existing</u>
						</b>{' '}
						tasks or projects.
					</Text>
				</FlexRow>
				<FlexRow gap={'s'}>
					<RadioButton
						checked={importMode === ImportMode.ImportPhasesTasksTimeRegs}
						onClick={() => setImportMode(ImportMode.ImportPhasesTasksTimeRegs)}
						disabled={disabled}
					/>
					<Text>
						Import phases, tasks or time entries into{' '}
						<b>
							<u>existing</u>
						</b>{' '}
						projects.
					</Text>
				</FlexRow>
			</SubSection>
		</Section>
	);
};

type ResultTextProps = {success: boolean};
const ResultText = ({success}: ResultTextProps) => {
	if (success) {
		return (
			<div className="bold">
				<p>Data successfully uploaded and will now be added to your account.</p>
				<p>
					The actual time it takes before you can see the data can vary, but smaller data sets should not take more
					than a couple of minutes to process, whereas larger data sets can potentially take hours before everything
					is ready for you.
				</p>
				<p>For reference 1000 time entries will take approximately 5 minutes to import.</p>
			</div>
		);
	} else {
		return (
			<div>
				<p>The import failed. Please reach out to our support for further assistance.</p>
			</div>
		);
	}
};

const SettingsDataImporter_viewerFragment = graphql`
	fragment SettingsDataImporter_viewer on Viewer {
		email
		company {
			id
			allCompanyTaskIds
			allCompanyProjectIds
			allPersons(first: 10000) {
				edges {
					node {
						id
						email
					}
				}
			}
			clients(first: 10000) {
				edges {
					node {
						id
						name
					}
				}
			}
			rateCards(first: 10000) {
				edges {
					node {
						id
						name
					}
				}
			}
			roles(first: 10000) {
				edges {
					node {
						id
						name
					}
				}
			}
		}
	}
`;

type SettingsDataImporterProps = {
	viewer: SettingsDataImporter_viewer;
};

export const SettingsDataImporter = ({viewer: viewerRef}: SettingsDataImporterProps): React.JSX.Element => {
	useTrackPage(PAGE_NAME);
	useEffect(() => {
		document.title = 'Data Importer - Account - Forecast';

		tracking.trackPage('settings-dataImporter');
	}, []);

	const [importMode, setImportMode] = useState(ImportMode.ImportNewProjects);

	const viewer = useFragment(SettingsDataImporter_viewerFragment, viewerRef);

	const [nuvoImporter, setNuvoImporter] = useState<NuvoImporter | null>(null);
	const [nuvoResult, setNuvoResult] = useState<NuvoImportResult | null>(null);
	const [submitLoading, setSubmitLoading] = useState(false);
	const [migrationStatus, setMigrationStatus] = useState<boolean | null>(null);

	const existingData = useMemo(
		() => ({
			emails: (viewer.company?.allPersons?.edges?.map(e => e?.node?.email).filter(Boolean) as string[]) ?? [],
			clients: (viewer.company?.clients?.edges?.map(e => e?.node?.name).filter(Boolean) as string[]) ?? [],
			rateCards: (viewer.company?.rateCards?.edges?.map(e => e?.node?.name).filter(Boolean) as string[]) ?? [],
			projects: viewer.company?.allCompanyProjectIds?.filter(Boolean).map((id: number) => '' + id) ?? [],
			tasks: viewer.company?.allCompanyTaskIds?.filter(Boolean).map((id: number) => '' + id) ?? [],
		}),
		[viewer]
	);

	const confirmDataUpload = async () => {
		setSubmitLoading(true);
		const headers = new Headers();
		headers.append('Content-Type', 'application/json');
		const data = {
			method: 'POST',
			headers,
			credentials: 'include' as const,
			body: JSON.stringify(nuvoResult),
		};
		await fetch(DirectApi.graphqlServerEndpoint(`data_migration_upload`), data).then(response => {
			setSubmitLoading(false);
			setMigrationStatus(response.ok);
			if (!response.ok) {
				Sentry.captureException(new Error('Data Importer Failed'), {
					tags: {bugduty: true, team: Team.Team1},
				});
			}
		});
	};

	const cancelDataUpload = async () => {
		setNuvoResult(null);
		window.location.reload();
	};

	const initializeDataImport = (mode: ImportMode) => {
		const clientOptions = existingData.clients.map(client => {
			return {
				label: client,
				value: client,
				type: 'string' as const,
			};
		});
		const rateCardOptions = existingData.rateCards.map(rateCard => {
			return {
				label: rateCard,
				value: rateCard,
				type: 'string' as const,
			};
		});

		let settings;
		if (mode === ImportMode.ImportNewProjects) {
			settings = settingsForNewProjectsAndTasks(clientOptions, rateCardOptions);
		} else if (mode === ImportMode.ImportTimeRegs) {
			settings = settingsForExistingProjectsAndTasks();
		} else if (mode === ImportMode.ImportPhasesTasksTimeRegs) {
			settings = settingsForNewPhasesTasksAndTimeRegsOnExistingProjects();
		}

		let key = '';
		if (process.env.CIRCLE_BRANCH === 'production' && !viewer.email?.includes('@forecast.')) {
			key = '+8GEeJk1ivE5xGFO6UiXIXwBOCkqqusXpqsvW3ilc78=';
			settings.developerMode = false;
		} else {
			key = 'KunfteTHBK0NElFNkgtu3ap0g/m4SJz6qtbLHXME1U8=';
		}

		const nuvoImporter = new window.nuvo.NuvoImporter(key, settings);
		nuvoImporter.onResults((results, _, complete) => {
			// Reserve the second parameter for the upcoming 'error' parameter.
			setNuvoResult(results);
			complete();
		});

		nuvoImporter.onCancel(() => cancelDataUpload());

		const validator = new DataImportValidator({
			nuvoImporter,
			importMode: mode,
			projects: existingData.projects.map(id => 'P' + id),
			tasks: existingData.tasks.map(id => 'T' + id),
			emails: existingData.emails.map(e => e.toLowerCase()),
			companyIdBase64: viewer.company?.id,
		});

		if (mode === ImportMode.ImportNewProjects) {
			validator.registerColumnHooksTaskEmails();
		} else if (mode === ImportMode.ImportTimeRegs) {
			validator.registerValidationForExistingTasksAndProjects();
		} else if (mode === ImportMode.ImportPhasesTasksTimeRegs) {
			validator.registerColumnHooksForExistingProjects();
			validator.registerColumnHooksTaskEmails();
		}

		validator.registerColumnHooksForTimeRegEmails();
		validator.registerEntryChange();

		try {
			nuvoImporter.launch();
		} catch (e) {
			// eslint-disable-next-line no-empty
		}
		const target = document.getElementById(nuvoImporter.getId()) as HTMLElement;
		const c = document.getElementById('nuvo-container');

		c?.append(target);

		const f = document.getElementsByTagName('iframe');

		if (window.innerHeight < 600) {
			f[0].style.height = '60vh';
		} else if (window.innerHeight < 900) {
			f[0].style.height = '70vh';
		} else {
			f[0].style.height = '80vh';
		}

		setNuvoImporter(nuvoImporter);
	};

	useEffect(() => {
		if (nuvoImporter === null) {
			loadScript('https://unpkg.com/nuvo-vanilla-js@2.8.0', () => initializeDataImport(importMode));
		} else {
			const target = document.getElementById(nuvoImporter.getId());
			target?.remove();
			initializeDataImport(importMode);
		}
	}, [importMode]);

	return (
		<div>
			<DescriptionStyled>
				<TopHeader />
				<div className="description">
					<ImportSelection importMode={importMode} setImportMode={setImportMode} disabled={nuvoResult !== null} />
				</div>
				{nuvoResult === null ? (
					<div className="my-nuvo-style" id="nuvo-container"></div>
				) : migrationStatus === null ? (
					<SubmitDataImport
						importMode={importMode}
						newData={nuvoResult}
						existingData={existingData}
						onSubmit={confirmDataUpload}
						onCancel={cancelDataUpload}
						loading={submitLoading}
					/>
				) : (
					<div className="sub-description">
						<ResultText success={migrationStatus} />
					</div>
				)}
			</DescriptionStyled>
		</div>
	);
};

const settingsDataImporterQuery = graphql`
	query SettingsDataImporter_Query {
		viewer {
			actualPersonId
			component(name: "data_importer")
			...SettingsDataImporter_viewer
		}
	}
`;

export {settingsDataImporterQuery};
